import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ToastProvider } from './components/Toast';
import Layout, { homeRouteForRole } from './components/Layout';
import Login from './routes/Login';
import ShpenzimetAktuale from './routes/ShpenzimetAktuale';
import ShpenzimetPlanifikuara from './routes/ShpenzimetPlanifikuara';
import ShpenzimetDevijimi from './routes/ShpenzimetDevijimi';
import ShpenzimetAutomatike from './routes/ShpenzimetAutomatike';
import ShpenzimetRaportet from './routes/ShpenzimetRaportet';
import Pagesat from './routes/Pagesat';
import Dashboard from './routes/Dashboard';
import Fitimet from './routes/Fitimet';
import FitimetKrahasimi from './routes/FitimetKrahasimi';
import FitimetRaportet from './routes/FitimetRaportet';
import Stafi from './routes/Stafi';
import RrogatScreen from './routes/Rrogat';
import RrogatDevijimi from './routes/RrogatDevijimi';
import Klientat from './routes/Klientat';
import Borxhet from './routes/Borxhet';
import Restorant from './routes/Restorant';
import Kalendar from './routes/Kalendar';
import Nenkategorit from './routes/Nenkategorit';
import Sop from './routes/Sop';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * The root path means different things per role, mirroring OpenFormBasedOnRole
 * (frmLogin.cs:75): Owner lands on the dashboard, Starbar on its single screen.
 */
function Home() {
  const { user } = useAuth();
  const home = homeRouteForRole(user?.role);
  if (home !== '/') return <Navigate to={home} replace />;
  return <Dashboard />;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted">Duke ngarkuar…</div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                element={
                  <RequireAuth>
                    <Layout />
                  </RequireAuth>
                }
              >
                <Route path="/" element={<Home />} />

                {/* Phase 1: the complete vertical slice. */}
                <Route
                  path="/shpenzimet/aktuale/:biznesi"
                  element={<ShpenzimetAktuale />}
                />

                {/* Phase 2: the rest of Shpenzimet, plus Pagesat. */}
                <Route
                  path="/shpenzimet/planifikuara/:biznesi"
                  element={<ShpenzimetPlanifikuara />}
                />
                <Route path="/shpenzimet/devijimi/:biznesi" element={<ShpenzimetDevijimi />} />
                <Route path="/shpenzimet/raportet/:biznesi" element={<ShpenzimetRaportet />} />
                <Route path="/shpenzimet/automatike" element={<ShpenzimetAutomatike />} />
                <Route path="/pagesat/aktuale" element={<Pagesat variant="aktuale" />} />
                <Route
                  path="/pagesat/planifikuara"
                  element={<Pagesat variant="planifikuara" />}
                />

                {/* Remaining screens, built in later phases. Routed now so the
                    navigation is complete and nothing dead-ends. */}
                {/* Phase 3: Fitimet. Fixed paths before /:biznesi, or
                    "krahasimi" and "raportet" get read as business slugs. */}
                <Route path="/fitimet/krahasimi" element={<FitimetKrahasimi />} />
                <Route path="/fitimet/raportet" element={<FitimetRaportet />} />
                <Route path="/fitimet/:biznesi" element={<Fitimet />} />
                {/* Phase 4: staff, salaries and gym memberships. */}
                <Route path="/stafi" element={<Stafi />} />
                <Route path="/rrogat" element={<RrogatScreen variant="rrogat" />} />
                <Route
                  path="/rrogat/planifikuara"
                  element={<RrogatScreen variant="rrogatPlan" />}
                />
                <Route path="/rrogat/devijimi" element={<RrogatDevijimi />} />
                <Route path="/klientat" element={<Navigate to="/klientat/stargym" replace />} />
                <Route path="/klientat/:biznesi" element={<Klientat />} />
                {/* Phase 5: the remaining screens. */}
                <Route path="/borxhet" element={<Borxhet />} />
                <Route path="/restorant" element={<Restorant />} />
                <Route path="/kalendar" element={<Kalendar />} />
                <Route
                  path="/nenkategorit"
                  element={<Navigate to="/nenkategorit/shpenzimet/stargym" replace />}
                />
                <Route
                  path="/nenkategorit/shpenzimet"
                  element={<Navigate to="/nenkategorit/shpenzimet/stargym" replace />}
                />
                <Route
                  path="/nenkategorit/shpenzimet/:biznesi"
                  element={<Nenkategorit kind="shpenzimet" />}
                />
                <Route
                  path="/nenkategorit/fitimet"
                  element={<Navigate to="/nenkategorit/fitimet/stargym" replace />}
                />
                <Route
                  path="/nenkategorit/fitimet/:biznesi"
                  element={<Nenkategorit kind="fitimet" />}
                />
                <Route path="/sop" element={<Sop />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
